import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiArrowRight, FiCheck } from 'react-icons/fi';

const EstimatorContainer = styled(motion.div)`
  background-color: var(--white);
  border-radius: var(--border-radius-md);
  box-shadow: var(--shadow-md);
  padding: 2rem;
  margin-top: 3rem;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    margin-top: 2rem;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
  }
  
  body.dark-mode & {
    background-color: #0e1420;
  }
`;

const EstimatorTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  color: var(--primary);
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
    margin-bottom: 1.2rem;
  }
  
  body.dark-mode & {
    color: var(--light);
  }
`;

const StepContainer = styled.div`
  margin-bottom: 2rem;
`;

const StepTitle = styled.h4`
  font-size: 1.2rem;
  margin-bottom: 1rem;
  color: var(--dark);
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  
  @media (max-width: 480px) {
    font-size: 1.05rem;
  }
  
  .step-number {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    background-color: var(--secondary);
    color: var(--black);
    border-radius: 50%;
    margin-right: 1rem;
    font-size: 0.9rem;
    font-weight: bold;
  }
  
  body.dark-mode & {
    color: var(--light);
  }
`;

const OptionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(${props => props.columns || 2}, 1fr);
  gap: 1rem;
  margin-bottom: 1rem;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const OptionButton = styled.button`
  background-color: ${props => props.selected ? 'var(--secondary)' : 'var(--gray-200)'};
  color: ${props => props.selected ? 'var(--black)' : 'var(--gray-700)'};
  border: 2px solid ${props => props.selected ? 'var(--secondary)' : 'transparent'};
  border-radius: var(--border-radius-sm);
  padding: 1.2rem 1rem;
  font-size: 0.9rem;
  font-weight: ${props => props.selected ? '600' : '400'};
  cursor: pointer;
  transition: all var(--transition-fast);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  min-height: 100px;
  text-align: center;
  
  @media (max-width: 480px) {
    padding: 1rem 0.8rem;
    min-height: 80px;
    font-size: 0.85rem;
  }
  
  &:hover {
    background-color: ${props => props.selected ? 'var(--secondary)' : 'var(--gray-300)'};
    transform: translateY(-2px);
    border-color: ${props => props.selected ? 'var(--secondary)' : 'var(--gray-400)'};
  }
  
  .check-icon {
    position: absolute;
    top: 8px;
    right: 8px;
    color: var(--black);
    font-size: 1.1rem;
  }
  
  .option-name {
    font-weight: 600;
    font-size: 1rem;
    margin-bottom: 0.3rem;
  }
  
  .option-desc {
    font-size: 0.8rem;
    opacity: 0.7;
    margin-bottom: 0.5rem;
  }
  
  .price-tag {
    font-size: 0.85rem;
    margin-top: 0.5rem;
    font-weight: 600;
    color: ${props => props.selected ? 'var(--black)' : 'var(--primary)'};
  }
  
  .delay-tag {
    font-size: 0.75rem;
    margin-top: 0.3rem;
    opacity: 0.6;
  }
  
  body.dark-mode & {
    background-color: ${props => props.selected ? 'var(--secondary)' : '#101828'};
    color: ${props => props.selected ? 'var(--black)' : 'var(--light)'};
    border-color: ${props => props.selected ? 'var(--secondary)' : 'transparent'};
    
    &:hover {
      background-color: ${props => props.selected ? 'var(--secondary)' : '#141c2e'};
      border-color: ${props => props.selected ? 'var(--secondary)' : 'rgba(255, 255, 255, 0.12)'};
    }
    
    .price-tag {
      color: ${props => props.selected ? 'var(--black)' : 'var(--secondary)'};
    }
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background-color: var(--gray-300);
  border-radius: 3px;
  margin-bottom: 0.5rem;
  overflow: hidden;
  
  body.dark-mode & {
    background-color: #101828;
  }
`;

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, var(--secondary), var(--accent));
  border-radius: 3px;
  transition: width 0.3s ease;
`;

const ProgressText = styled.div`
  text-align: center;
  font-size: 0.85rem;
  color: var(--gray-600);
  margin-bottom: 2rem;
  
  body.dark-mode & {
    color: var(--gray-400);
  }
`;

const IncludedInfo = styled.div`
  background-color: rgba(52, 168, 83, 0.1);
  color: #34A853;
  padding: 0.75rem 1rem;
  border-radius: var(--border-radius-sm);
  font-size: 0.9rem;
  margin-bottom: 1.5rem;
  border-left: 3px solid #34A853;
  
  body.dark-mode & {
    background-color: rgba(52, 168, 83, 0.15);
  }
`;

const SubStepTitle = styled.h5`
  font-size: 1rem;
  color: var(--dark);
  margin-bottom: 1rem;
  font-weight: 600;
  
  body.dark-mode & {
    color: var(--light);
  }
`;

const NavigationButtons = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--gray-300);
  
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 0.8rem;
  }
  
  body.dark-mode & {
    border-color: #2a4060;
  }
`;

const NavButton = styled.button`
  padding: 0.8rem 1.5rem;
  border-radius: var(--border-radius-sm);
  font-weight: 600;
  cursor: pointer;
  transition: all var(--transition-fast);
  
  @media (max-width: 480px) {
    width: 100%;
    padding: 1rem;
  }
  
  ${props => props.type === 'primary' ? `
    background-color: var(--secondary);
    color: var(--black);
    border: none;
    margin-left: auto;
    
    &:hover {
      background-color: var(--accent);
      color: var(--white);
      transform: translateY(-2px);
    }
  ` : `
    background-color: transparent;
    color: var(--gray-600);
    border: 1px solid var(--gray-400);
    
    &:hover {
      background-color: var(--gray-200);
      border-color: var(--gray-500);
    }
  `}
  
  body.dark-mode & {
    ${props => props.type !== 'primary' && `
      color: var(--gray-400);
      border-color: #2a4060;
      
      &:hover {
        background-color: #1e3050;
        border-color: #345070;
      }
    `}
  }
`;

const Disclaimer = styled.p`
  font-size: 0.75rem;
  color: var(--gray-500);
  margin-top: 1.5rem;
  line-height: 1.5;
  font-style: italic;
  
  body.dark-mode & {
    color: rgba(255, 255, 255, 0.6);
  }
`;

const RangeContainer = styled.div`
  margin-bottom: 1rem;
`;

const RangeLabels = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  
  span {
    font-size: 0.8rem;
    color: var(--gray-600);
    
    body.dark-mode & {
      color: var(--gray-400);
    }
  }
`;

const RangeInput = styled.input`
  width: 100%;
  -webkit-appearance: none;
  height: 8px;
  border-radius: 5px;
  background: var(--gray-300);
  outline: none;
  
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: var(--secondary);
    cursor: pointer;
    transition: all var(--transition-fast);
    
    &:hover {
      transform: scale(1.2);
    }
  }
  
  &::-moz-range-thumb {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: var(--secondary);
    cursor: pointer;
    border: none;
    transition: all var(--transition-fast);
    
    &:hover {
      transform: scale(1.2);
    }
  }
  
  body.dark-mode & {
    background: #1e3050;
  }
`;

const RangeValue = styled.div`
  text-align: center;
  font-weight: 600;
  margin-top: 0.5rem;
  color: var(--dark);
  
  body.dark-mode & {
    color: var(--light);
  }
`;

const ResultContainer = styled(motion.div)`
  background: var(--gray-200);
  color: var(--dark);
  padding: 2rem;
  border-radius: var(--border-radius-md);
  margin-top: 2rem;
  position: relative;
  overflow: hidden;
  
  body.dark-mode & {
    background: #1e3050;
    color: var(--white);
  }
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

const ResultTitle = styled.h4`
  font-size: 1.2rem;
  margin-bottom: 1rem;
  position: relative;
  z-index: 1;
  color: var(--dark);
  font-weight: 700;
  
  body.dark-mode & {
    color: var(--white);
  }
`;

const PriceRange = styled.div`
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  position: relative;
  z-index: 1;
  text-align: center;
  color: var(--dark);
  
  body.dark-mode & {
    color: var(--white);
  }
  
  &::after {
    content: '';
    display: block;
    width: 50px;
    height: 4px;
    background-color: var(--secondary);
    margin: 0.5rem auto;
    border-radius: 2px;
  }
`;

const ResultDetails = styled.ul`
  list-style-type: none;
  margin-top: 1.5rem;
  position: relative;
  z-index: 1;
`;

const ResultDetail = styled.li`
  margin-bottom: 0.8rem;
  display: flex;
  align-items: flex-start;
  color: var(--dark);
  
  body.dark-mode & {
    color: var(--white);
  }
  
  svg {
    color: var(--secondary);
    margin-right: 0.8rem;
    flex-shrink: 0;
    margin-top: 0.3rem;
  }
`;

const ContactButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background-color: var(--secondary);
  color: var(--black);
  border: none;
  border-radius: var(--border-radius-sm);
  padding: 0.8rem 1.5rem;
  font-weight: 600;
  margin-top: 1.5rem;
  cursor: pointer;
  transition: all var(--transition-fast);
  width: 100%;
  position: relative;
  z-index: 1;
  
  &:hover {
    background-color: var(--accent);
    color: var(--white);
    transform: translateY(-3px);
  }
  
  svg {
    font-size: 1.2rem;
  }
`;

const BelgianAccent = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: 30px;
  height: 100%;
  display: flex;
  flex-direction: column;
  z-index: 0;
  
  .black, .yellow, .red {
    flex: 1;
  }
  
  .black {
    background-color: var(--black);
  }
  
  .yellow {
    background-color: var(--secondary);
  }
  
  .red {
    background-color: var(--accent);
  }
`;

const QuoteEstimator = ({ scrollToContact }) => {
  // Types de sites avec leurs prix de base (prix réalistes marché belge/français)
  const siteTypes = [
    { id: 'landing', name: 'Landing Page', description: '1 page optimisée conversion', price: 800, delay: '1-2 semaines' },
    { id: 'vitrine-small', name: 'Site Vitrine Simple', description: '3-5 pages', price: 1500, delay: '2-3 semaines' },
    { id: 'vitrine-pro', name: 'Site Vitrine Pro', description: '6-10 pages', price: 2800, delay: '3-5 semaines' },
    { id: 'ecommerce-small', name: 'E-commerce Starter', description: 'Jusqu\'à 50 produits', price: 3500, delay: '4-6 semaines' },
    { id: 'ecommerce-pro', name: 'E-commerce Pro', description: '50-500 produits', price: 5500, delay: '6-10 semaines' },
    { id: 'webapp', name: 'Application Web', description: 'Sur mesure', price: 6000, delay: '8-12 semaines' }
  ];

  // Fonctionnalités de design
  const designOptions = [
    { id: 'template', name: 'Template personnalisé', description: 'Basé sur un modèle', price: 0 },
    { id: 'custom-design', name: 'Design sur mesure', description: 'Création unique', price: 800 },
    { id: 'premium-design', name: 'Design Premium', description: 'Maquettes complètes + révisions illimitées', price: 1500 }
  ];

  // Fonctionnalités techniques
  const technicalFeatures = [
    { id: 'responsive', name: 'Responsive design', price: 0, included: true },
    { id: 'contact-form', name: 'Formulaire de contact', price: 0, included: true },
    { id: 'advanced-forms', name: 'Formulaires avancés', description: 'Devis, réservation, multi-étapes', price: 300 },
    { id: 'blog', name: 'Section Blog', description: 'Avec catégories et recherche', price: 400 },
    { id: 'newsletter', name: 'Newsletter', description: 'Inscription + intégration Mailchimp/Sendinblue', price: 250 },
    { id: 'booking', name: 'Système de réservation', description: 'Calendrier + paiement en ligne', price: 800 },
    { id: 'members', name: 'Espace membres', description: 'Connexion, profils, contenu privé', price: 1000 },
    { id: 'payment', name: 'Paiement en ligne', description: 'Stripe, PayPal, Bancontact', price: 400 },
    { id: 'chat', name: 'Chat en direct', description: 'Support client intégré', price: 200 },
    { id: 'animations', name: 'Animations avancées', description: 'Micro-interactions, effets visuels', price: 400 }
  ];

  // SEO et Marketing
  const marketingOptions = [
    { id: 'seo-base', name: 'SEO de base', price: 0, included: true },
    { id: 'seo-advanced', name: 'SEO avancé', description: 'Optimisation complète + stratégie mots-clés', price: 600 },
    { id: 'seo-local', name: 'SEO local', description: 'Google My Business + citations locales', price: 350 },
    { id: 'analytics', name: 'Analytics avancés', description: 'Google Analytics 4 + rapports personnalisés', price: 250 },
    { id: 'social', name: 'Intégration réseaux sociaux', description: 'Flux, partage, pixels', price: 200 },
    { id: 'speed', name: 'Optimisation vitesse', description: 'Core Web Vitals + CDN', price: 300 }
  ];

  // Contenu
  const contentOptions = [
    { id: 'content-basic', name: 'Intégration de votre contenu', price: 0, included: true },
    { id: 'copywriting', name: 'Rédaction de contenu', description: 'Textes professionnels optimisés SEO', price: 500 },
    { id: 'translation', name: 'Traduction', description: 'Par langue supplémentaire', price: 400 },
    { id: 'photos-stock', name: 'Photos professionnelles', description: 'Banque d\'images premium', price: 150 },
    { id: 'photos-custom', name: 'Shooting photo', description: 'Séance photo sur site', price: 600 },
    { id: 'video', name: 'Vidéo de présentation', description: 'Montage professionnel', price: 800 }
  ];

  // Maintenance et support
  const maintenanceOptions = [
    { id: 'maintenance-none', name: 'Sans maintenance', price: 0 },
    { id: 'maintenance-basic', name: 'Maintenance Essentielle', description: 'Mises à jour + sauvegardes (1 an)', price: 480 },
    { id: 'maintenance-pro', name: 'Maintenance Pro', description: 'Mises à jour + support prioritaire + modifications mineures (1 an)', price: 960 },
    { id: 'maintenance-premium', name: 'Maintenance Premium', description: 'Tout inclus + rapport mensuel + améliorations (1 an)', price: 1800 }
  ];

  // Hébergement
  const hostingOptions = [
    { id: 'hosting-client', name: 'Votre hébergement', description: 'Vous gérez l\'hébergement', price: 0 },
    { id: 'hosting-basic', name: 'Hébergement Standard', description: 'Serveur partagé + SSL (1 an)', price: 120 },
    { id: 'hosting-pro', name: 'Hébergement Pro', description: 'Serveur dédié + SSL + CDN (1 an)', price: 300 },
    { id: 'hosting-managed', name: 'Hébergement Géré', description: 'Tout inclus + monitoring 24/7 (1 an)', price: 600 }
  ];

  const [selectedType, setSelectedType] = useState(siteTypes[1].id);
  const [selectedDesign, setSelectedDesign] = useState(designOptions[0].id);
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [selectedMarketing, setSelectedMarketing] = useState([]);
  const [selectedContent, setSelectedContent] = useState([]);
  const [selectedMaintenance, setSelectedMaintenance] = useState(maintenanceOptions[0].id);
  const [selectedHosting, setSelectedHosting] = useState(hostingOptions[0].id);
  const [expressDelivery, setExpressDelivery] = useState(false);
  const [totalPrice, setTotalPrice] = useState({ min: 0, max: 0 });
  const [currentStep, setCurrentStep] = useState(1);
  
  const totalSteps = 7;

  // Calculer le prix total en fonction des sélections
  useEffect(() => {
    // Prix de base selon le type de site
    const siteType = siteTypes.find(type => type.id === selectedType);
    let basePrice = siteType.price;
    
    // Prix du design
    const design = designOptions.find(opt => opt.id === selectedDesign);
    basePrice += design.price;
    
    // Prix des fonctionnalités techniques
    const featuresPrice = selectedFeatures.reduce((total, optionId) => {
      const option = technicalFeatures.find(opt => opt.id === optionId);
      return total + (option ? option.price : 0);
    }, 0);
    
    // Prix marketing
    const marketingPrice = selectedMarketing.reduce((total, optionId) => {
      const option = marketingOptions.find(opt => opt.id === optionId);
      return total + (option ? option.price : 0);
    }, 0);
    
    // Prix contenu
    const contentPrice = selectedContent.reduce((total, optionId) => {
      const option = contentOptions.find(opt => opt.id === optionId);
      return total + (option ? option.price : 0);
    }, 0);
    
    // Prix maintenance
    const maintenance = maintenanceOptions.find(opt => opt.id === selectedMaintenance);
    const maintenancePrice = maintenance.price;
    
    // Prix hébergement
    const hosting = hostingOptions.find(opt => opt.id === selectedHosting);
    const hostingPrice = hosting.price;
    
    // Prix total avant majoration express
    let finalPrice = basePrice + featuresPrice + marketingPrice + contentPrice + maintenancePrice + hostingPrice;
    
    // Majoration pour livraison express (+30%)
    if (expressDelivery) {
      finalPrice = finalPrice * 1.3;
    }
    
    // Fourchette de prix (-10% / +15% pour les imprévus)
    setTotalPrice({
      min: Math.round(finalPrice * 0.9),
      max: Math.round(finalPrice * 1.15)
    });
  }, [selectedType, selectedDesign, selectedFeatures, selectedMarketing, selectedContent, selectedMaintenance, selectedHosting, expressDelivery]);
  
  const toggleOption = (optionId, setFunction, currentArray) => {
    if (currentArray.includes(optionId)) {
      setFunction(currentArray.filter(id => id !== optionId));
    } else {
      setFunction([...currentArray, optionId]);
    }
  };

  const nextStep = () => {
    if (currentStep < totalSteps) setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };
  
  return (
    <EstimatorContainer
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8 }}
    >
      <EstimatorTitle>Simulateur de devis</EstimatorTitle>
      
      {/* Barre de progression */}
      <ProgressBar>
        <ProgressFill style={{ width: `${(currentStep / totalSteps) * 100}%` }} />
      </ProgressBar>
      <ProgressText>Étape {currentStep} sur {totalSteps}</ProgressText>
      
      {/* Étape 1: Type de site */}
      {currentStep === 1 && (
        <StepContainer>
          <StepTitle>
            <span className="step-number">1</span>
            Quel type de site souhaitez-vous ?
          </StepTitle>
          <OptionGrid columns={3}>
            {siteTypes.map(type => (
              <OptionButton 
                key={type.id}
                selected={selectedType === type.id} 
                onClick={() => setSelectedType(type.id)}
              >
                <span className="option-name">{type.name}</span>
                <span className="option-desc">{type.description}</span>
                <div className="price-tag">À partir de {type.price} €</div>
                <span className="delay-tag">⏱ {type.delay}</span>
                {selectedType === type.id && <FiCheck className="check-icon" />}
              </OptionButton>
            ))}
          </OptionGrid>
        </StepContainer>
      )}

      {/* Étape 2: Design */}
      {currentStep === 2 && (
        <StepContainer>
          <StepTitle>
            <span className="step-number">2</span>
            Quel niveau de design ?
          </StepTitle>
          <OptionGrid columns={3}>
            {designOptions.map(option => (
              <OptionButton 
                key={option.id}
                selected={selectedDesign === option.id} 
                onClick={() => setSelectedDesign(option.id)}
              >
                <span className="option-name">{option.name}</span>
                <span className="option-desc">{option.description}</span>
                <div className="price-tag">{option.price === 0 ? 'Inclus' : `+${option.price} €`}</div>
                {selectedDesign === option.id && <FiCheck className="check-icon" />}
              </OptionButton>
            ))}
          </OptionGrid>
        </StepContainer>
      )}

      {/* Étape 3: Fonctionnalités techniques */}
      {currentStep === 3 && (
        <StepContainer>
          <StepTitle>
            <span className="step-number">3</span>
            Fonctionnalités techniques
          </StepTitle>
          <IncludedInfo>✓ Responsive design et formulaire de contact inclus de base</IncludedInfo>
          <OptionGrid columns={2}>
            {technicalFeatures.filter(f => !f.included).map(option => (
              <OptionButton 
                key={option.id}
                selected={selectedFeatures.includes(option.id)} 
                onClick={() => toggleOption(option.id, setSelectedFeatures, selectedFeatures)}
              >
                <span className="option-name">{option.name}</span>
                {option.description && <span className="option-desc">{option.description}</span>}
                <div className="price-tag">+{option.price} €</div>
                {selectedFeatures.includes(option.id) && <FiCheck className="check-icon" />}
              </OptionButton>
            ))}
          </OptionGrid>
        </StepContainer>
      )}

      {/* Étape 4: SEO et Marketing */}
      {currentStep === 4 && (
        <StepContainer>
          <StepTitle>
            <span className="step-number">4</span>
            SEO et Marketing
          </StepTitle>
          <IncludedInfo>✓ SEO de base inclus (balises meta, sitemap, robots.txt)</IncludedInfo>
          <OptionGrid columns={2}>
            {marketingOptions.filter(f => !f.included).map(option => (
              <OptionButton 
                key={option.id}
                selected={selectedMarketing.includes(option.id)} 
                onClick={() => toggleOption(option.id, setSelectedMarketing, selectedMarketing)}
              >
                <span className="option-name">{option.name}</span>
                {option.description && <span className="option-desc">{option.description}</span>}
                <div className="price-tag">+{option.price} €</div>
                {selectedMarketing.includes(option.id) && <FiCheck className="check-icon" />}
              </OptionButton>
            ))}
          </OptionGrid>
        </StepContainer>
      )}

      {/* Étape 5: Contenu */}
      {currentStep === 5 && (
        <StepContainer>
          <StepTitle>
            <span className="step-number">5</span>
            Contenu et médias
          </StepTitle>
          <IncludedInfo>✓ Intégration de votre contenu existant incluse</IncludedInfo>
          <OptionGrid columns={2}>
            {contentOptions.filter(f => !f.included).map(option => (
              <OptionButton 
                key={option.id}
                selected={selectedContent.includes(option.id)} 
                onClick={() => toggleOption(option.id, setSelectedContent, selectedContent)}
              >
                <span className="option-name">{option.name}</span>
                {option.description && <span className="option-desc">{option.description}</span>}
                <div className="price-tag">+{option.price} €</div>
                {selectedContent.includes(option.id) && <FiCheck className="check-icon" />}
              </OptionButton>
            ))}
          </OptionGrid>
        </StepContainer>
      )}

      {/* Étape 6: Maintenance et Hébergement */}
      {currentStep === 6 && (
        <StepContainer>
          <StepTitle>
            <span className="step-number">6</span>
            Maintenance et Hébergement
          </StepTitle>
          
          <SubStepTitle>Maintenance (première année)</SubStepTitle>
          <OptionGrid columns={2}>
            {maintenanceOptions.map(option => (
              <OptionButton 
                key={option.id}
                selected={selectedMaintenance === option.id} 
                onClick={() => setSelectedMaintenance(option.id)}
              >
                <span className="option-name">{option.name}</span>
                {option.description && <span className="option-desc">{option.description}</span>}
                <div className="price-tag">{option.price === 0 ? 'Non inclus' : `${option.price} €/an`}</div>
                {selectedMaintenance === option.id && <FiCheck className="check-icon" />}
              </OptionButton>
            ))}
          </OptionGrid>

          <SubStepTitle style={{ marginTop: '2rem' }}>Hébergement (première année)</SubStepTitle>
          <OptionGrid columns={2}>
            {hostingOptions.map(option => (
              <OptionButton 
                key={option.id}
                selected={selectedHosting === option.id} 
                onClick={() => setSelectedHosting(option.id)}
              >
                <span className="option-name">{option.name}</span>
                {option.description && <span className="option-desc">{option.description}</span>}
                <div className="price-tag">{option.price === 0 ? 'Non inclus' : `${option.price} €/an`}</div>
                {selectedHosting === option.id && <FiCheck className="check-icon" />}
              </OptionButton>
            ))}
          </OptionGrid>
        </StepContainer>
      )}

      {/* Étape 7: Délai et Récapitulatif */}
      {currentStep === 7 && (
        <StepContainer>
          <StepTitle>
            <span className="step-number">7</span>
            Délai de livraison
          </StepTitle>
          <OptionGrid columns={2}>
            <OptionButton 
              selected={!expressDelivery} 
              onClick={() => setExpressDelivery(false)}
            >
              <span className="option-name">Délai standard</span>
              <span className="option-desc">{siteTypes.find(t => t.id === selectedType)?.delay}</span>
              <div className="price-tag">Inclus</div>
              {!expressDelivery && <FiCheck className="check-icon" />}
            </OptionButton>
            <OptionButton 
              selected={expressDelivery} 
              onClick={() => setExpressDelivery(true)}
            >
              <span className="option-name">Livraison express</span>
              <span className="option-desc">Délai réduit de 30-40%</span>
              <div className="price-tag">+30% du total</div>
              {expressDelivery && <FiCheck className="check-icon" />}
            </OptionButton>
          </OptionGrid>
        </StepContainer>
      )}

      {/* Navigation entre étapes */}
      <NavigationButtons>
        {currentStep > 1 && (
          <NavButton onClick={prevStep} type="secondary">
            ← Précédent
          </NavButton>
        )}
        {currentStep < totalSteps ? (
          <NavButton onClick={nextStep} type="primary">
            Suivant →
          </NavButton>
        ) : null}
      </NavigationButtons>
      
      {/* Résultat toujours visible */}
      <ResultContainer
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
      >
        <ResultTitle>Estimation de votre projet</ResultTitle>
        <PriceRange>
          {totalPrice.min.toLocaleString()} € - {totalPrice.max.toLocaleString()} €
        </PriceRange>
        
        <ResultDetails>
          <ResultDetail>
            <FiCheck />
            <span><strong>Type :</strong> {siteTypes.find(type => type.id === selectedType)?.name}</span>
          </ResultDetail>
          <ResultDetail>
            <FiCheck />
            <span><strong>Design :</strong> {designOptions.find(opt => opt.id === selectedDesign)?.name}</span>
          </ResultDetail>
          {selectedFeatures.length > 0 && (
            <ResultDetail>
              <FiCheck />
              <span><strong>Fonctionnalités :</strong> {selectedFeatures.map(optId => 
                technicalFeatures.find(opt => opt.id === optId)?.name
              ).join(', ')}</span>
            </ResultDetail>
          )}
          {selectedMarketing.length > 0 && (
            <ResultDetail>
              <FiCheck />
              <span><strong>Marketing :</strong> {selectedMarketing.map(optId => 
                marketingOptions.find(opt => opt.id === optId)?.name
              ).join(', ')}</span>
            </ResultDetail>
          )}
          {selectedContent.length > 0 && (
            <ResultDetail>
              <FiCheck />
              <span><strong>Contenu :</strong> {selectedContent.map(optId => 
                contentOptions.find(opt => opt.id === optId)?.name
              ).join(', ')}</span>
            </ResultDetail>
          )}
          {selectedMaintenance !== 'maintenance-none' && (
            <ResultDetail>
              <FiCheck />
              <span><strong>Maintenance :</strong> {maintenanceOptions.find(opt => opt.id === selectedMaintenance)?.name}</span>
            </ResultDetail>
          )}
          {selectedHosting !== 'hosting-client' && (
            <ResultDetail>
              <FiCheck />
              <span><strong>Hébergement :</strong> {hostingOptions.find(opt => opt.id === selectedHosting)?.name}</span>
            </ResultDetail>
          )}
          {expressDelivery && (
            <ResultDetail>
              <FiCheck />
              <span><strong>Livraison express</strong> (+30%)</span>
            </ResultDetail>
          )}
        </ResultDetails>

        <Disclaimer>
          * Cette estimation est indicative et basée sur les informations fournies. Le prix final sera déterminé après analyse détaillée de votre projet. Les tarifs peuvent varier selon la complexité réelle.
        </Disclaimer>
        
        <ContactButton onClick={scrollToContact}>
          Demander un devis personnalisé <FiArrowRight />
        </ContactButton>
      </ResultContainer>
    </EstimatorContainer>
  );
};

export default QuoteEstimator;
